import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import Banner from "../../components/Banner";
import Header from "../../components/Header";
import NewsSlider from "../../components/NewsSlider";
import Footer from "../../components/Footer";
import backgroundImage from "../../img/banner/ZTO-Banner.png";
import newsImage1 from "../../img/default-image.jpg";
import "../../css/newsDetail.css";
import CTASection from "../../components/CTASection";
import { API_URL_All } from "../../api";

const NewsDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [activeSection, setActiveSection] = useState("");
  const contentRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const dataLinks = [
    { categorySlug: "tin-tuc-thi-truong", path: "/market-news" },
    { categorySlug: "kien-thuc-xnk", path: "/knowledge-news" },
    { categorySlug: "nguon-hang", path: "/source-news" },
  ];

  const mapArticle = (item) => {
    // Guard against null/undefined items
    if (!item || typeof item !== 'object') {
      return null;
    }

    const content = Array.isArray(item?.content)
      ? item.content.map((section, idx) => ({
          ...section,
          id: section.id || `section-${idx}`,
          title: section.title || ``,
          content: section.content || "",
          subSections: Array.isArray(section.subSections)
            ? section.subSections.map((sub, subIdx) => ({
                ...sub,
                id: sub.id || `section-${idx}-sub-${subIdx}`,
                title: sub.title || ``,
                content: sub.content || "",
              }))
            : [],
        }))
      : [];

    return {
      ...item,
      image: item?.imageUrl || item?.image || newsImage1,
      date: item?.publishDate || item?.date || "",
      category: item?.category || item?.categoryLabel || "Tin tức",
      categorySlug: item?.categorySlug || item?.category || "",
      author: item?.author || "ZTO",
      showTableOfContents: item?.showTableOfContents ?? true,
      content,
      excerpt: item?.excerpt || "",
      tags: item?.tags || [],
      referenceLinks: item?.referenceLinks || [],
      cta: item?.id ? {
        href: `/news/${item.id}`,
        label: "Xem chi tiết"
      } : null
    };
  };

  // Scroll to section helper function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    const fetchArticle = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.get(`${API_URL_All}/api/news/${id}`);
        const mappedArticle = mapArticle(res.data);
        
        if (!mappedArticle) {
          if (isMounted) {
            setError(true);
            setArticle(null);
            setRelatedArticles([]);
          }
          return;
        }
        
        if (!isMounted) return;
        setArticle(mappedArticle);

        // fetch related by category
        if (mappedArticle?.categorySlug) {
          const relatedRes = await axios.get(`${API_URL_All}/api/news`, {
            params: { category: mappedArticle.categorySlug },
          });
          const rel = (relatedRes.data || [])
            .filter((item) => `${item.id}` !== `${mappedArticle.id}`)
            .map(mapArticle)
            .filter(Boolean);
          setRelatedArticles(rel.slice(0, 6));
        } else {
          setRelatedArticles([]);
        }
      } catch (err) {
        console.error("Error loading article", err);
        if (isMounted) {
          setError(true);
          setArticle(null);
          setRelatedArticles([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchArticle();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const tableOfContents = article
    && article.showTableOfContents
    ? article.content.reduce((acc, section) => {
        // Add main section
        acc.push({
          id: section.id,
          title: section.title,
          isSubSection: false,
        });
        
        // Add sub-sections if they exist
        if (section.subSections && section.subSections.length > 0) {
          section.subSections.forEach((subSection) => {
            acc.push({
              id: subSection.id,
              title: subSection.title,
              isSubSection: true,
            });
          });
        }
        
        return acc;
      }, [])
    : [];

  // Scroll spy - track active section
  useEffect(() => {
    if (!article || !article.content || !article.showTableOfContents) {
      setActiveSection("");
      return;
    }

    const handleScroll = () => {
      const allSections = [];
      
      // Collect all sections and sub-sections
      article.content.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          allSections.push({
            id: section.id,
            top: rect.top,
            bottom: rect.bottom,
          });
        }
        
        // Add sub-sections
        if (section.subSections && section.subSections.length > 0) {
          section.subSections.forEach((subSection) => {
            const subElement = document.getElementById(subSection.id);
            if (subElement) {
              const subRect = subElement.getBoundingClientRect();
              allSections.push({
                id: subSection.id,
                top: subRect.top,
                bottom: subRect.bottom,
              });
            }
          });
        }
      });

      const current = allSections.find(
        (section) => section.top <= 150 && section.bottom > 150
      );

      if (current) {
        setActiveSection(current.id);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [article]);

  const categoryPath = article
    ? dataLinks.find((link) => link.categorySlug === article?.categorySlug)?.path || "/news"
    : "/news";

  return (
    <div className="news-detail-page">
      <Header />

      <Banner
        title="Tin tức"
        backgroundImage={backgroundImage}
        breadcrumb={
          <>
            <Link to={"/"} className="text-white p-2">
              Trang chủ
            </Link>
            <span>{">"}</span>
            <Link to={"/news"} className="text-white p-2">
              Tin tức
            </Link>
            <span>{">"}</span>
            {article ? (
              <Link to={categoryPath} className="text-white p-2">
                {article?.category || "Tin tức"}
              </Link>
            ) : (
              <span className="text-white p-2">Tin tức</span>
            )}
          </>
        }
      />

      <div className="article-section py-5">
        <div className="container-fluid">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error || !article ? (
            <div className="text-center py-5">
              <p className="text-muted mb-3">
                {error
                  ? "Có lỗi khi tải bài viết. Vui lòng thử lại sau."
                  : "Không tìm thấy bài viết. Vui lòng quay lại trang tin tức."}
              </p>
              <Link to="/news" className="btn btn-primary">
                Về trang tin tức
              </Link>
            </div>
          ) : (
            <div className={`row ${article.showTableOfContents && tableOfContents.length > 0 ? "" : "justify-content-center"}`}>
              {article.showTableOfContents && tableOfContents.length > 0 && (
                <div className="col-lg-4 col-md-5">
                  <div className="sticky-sidebar bg-white rounded-3 p-4 shadow-sm">
                    <h4 className="toc-title">Mục lục</h4>
                    <ul className="toc-list list-unstyled">
                      {tableOfContents.map((item, index) => {
                        let displayNumber;
                        if (item.isSubSection) {
                          let parentIndex = 0;
                          let subIndex = 0;
                          for (let i = index - 1; i >= 0; i--) {
                            if (!tableOfContents[i].isSubSection) {
                              parentIndex = tableOfContents.slice(0, i + 1).filter(t => !t.isSubSection).length;
                              subIndex = index - i;
                              break;
                            }
                          }
                          displayNumber = `${parentIndex}.${subIndex}`;
                        } else {
                          displayNumber = tableOfContents.slice(0, index + 1).filter(t => !t.isSubSection).length;
                        }

                        return (
                          <li
                            key={item.id}
                            className={`mb-3 ${activeSection === item.id ? "active" : ""} ${item.isSubSection ? "ms-4" : ""}`}
                          >
                            <a
                              href={`#${item.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToSection(item.id);
                              }}
                              className="text-decoration-none text-dark d-flex align-items-start p-2 rounded-2"
                            >
                              <span className="toc-number fw-semibold" style={{ minWidth: "35px" }}>{displayNumber}.</span>
                              <span className={`toc-text flex-grow-1 lh-base ${item.isSubSection ? "fw-normal" : ""}`}>{item.title}</span>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              <div className={article.showTableOfContents && tableOfContents.length > 0 ? "col-lg-7 col-md-7" : "col-lg-8 col-md-7"}>
                <article className="bg-white rounded-3 p-4 p-md-5 shadow-sm" ref={contentRef}>
                  <div className="mb-3">
                    <div className="category-badge d-inline-block px-4 py-2 rounded-pill fw-semibold">{article.category}</div>
                  </div>

                  <h1 className="article-title fw-bold lh-sm my-4" style={{fontSize: '2.5rem'}}>{article.title}</h1>

                  <div className="article-meta d-flex align-items-center flex-wrap gap-2 text-secondary pb-3 border-bottom">
                    <span className="meta-item d-flex align-items-center gap-2">
                      <i className="far fa-calendar"></i> {article.date}
                    </span>
                    <span className="meta-separator">•</span>
                    <span className="meta-item d-flex align-items-center gap-2">
                      <i className="far fa-user"></i> {article.author}
                    </span>
                    {/* <span className="meta-separator">•</span>
                    <span className="meta-item d-flex align-items-center gap-2">
                      <i className="far fa-clock"></i> 5 phút đọc
                    </span> */}
                  </div>

                  <div className="featured-image w-100 rounded-3 overflow-hidden my-4">
                    <img src={article.image} alt={article.title} className="w-100 h-auto d-block" style={{objectFit: 'cover'}} />
                  </div>

                  {article.excerpt && (
                    <div className="article-excerpt rounded-2 p-2 my-4 shadow-sm">
                      <p className="m-0 fs-5 lh-lg text-dark fst-italic">{article.excerpt}</p>
                    </div>
                  )}
                    
                  <div className="article-body fs-5 lh-lg text-dark">
                    {article.content.map((section, idx) => {
                      const sectionNumber = idx + 1;
                      
                      return (
                        <div key={section.id || idx}>
                          <section
                            id={section.id}
                            className="content-section"
                          >
                            <h2 className="section-title fs-3 fw-bold">
                              {section.title}
                            </h2>
                            <div
                              className="section-content fs-6"
                              dangerouslySetInnerHTML={{ __html: section.content }}
                            />
                          </section>
                          
                          {/* Render sub-sections */}
                          {section.subSections && section.subSections.length > 0 && (
                            <div className="subsections-container ms-4">
                              {section.subSections.map((subSection, subIdx) => {
                                const subSectionNumber = `${sectionNumber}.${subIdx + 1}`;
                                
                                return (
                                  <section
                                    key={subSection.id || subIdx}
                                    id={subSection.id}
                                    className="content-subsection mt-4"
                                  >
                                    <h3 className="subsection-title fs-4 fw-semibold pb-2 text-zto">
                                      {subSectionNumber}. {subSection.title}
                                    </h3>
                                    <div
                                      className="subsection-content fs-6"
                                      dangerouslySetInnerHTML={{ __html: subSection.content }}
                                    />
                                  </section>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="article-tags pt-4">
                    <h5 className="fs-6 fw-semibold text-dark mb-3">Từ khóa:</h5>
                    <div className="tags-list d-flex flex-wrap gap-2">
                      {article.tags && article.tags.length > 0 ? (
                        article.tags.map((tag, index) => {
                          // Handle backward compatibility: support both string and object tags
                          const tagText = typeof tag === 'string' ? tag : tag.text;
                          return tagText ? (
                            <Link
                              key={index}
                              to={`/news?tag=${encodeURIComponent(tagText)}`}
                              className="tag-item d-inline-block px-3 py-2 rounded-pill text-decoration-none"
                              style={{ cursor: "pointer" }}
                            >
                              {tagText}
                            </Link>
                          ) : (
                            <span key={index} className="tag-item d-inline-block px-3 py-2 rounded-pill text-decoration-none">
                              {tagText}
                            </span>
                          );
                        })
                      ) : (
                        <>
                          {/* <Link to="#" className="tag-item">
                            Vận chuyển express
                          </Link>
                          <Link to="#" className="tag-item">
                            ZTO Việt Nam
                          </Link>
                          <Link to="#" className="tag-item">
                            Giao hàng nhanh
                          </Link>
                          <Link to="#" className="tag-item">
                            Logistics Việt Nam
                          </Link> */}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Reference Links Section */}
                  {article.referenceLinks && article.referenceLinks.length > 0 && (
                    <div className="article-references pt-4">
                      <h5 className="fs-3 fw-semibold text-dark mb-3">Có thể bạn sẽ thích</h5>
                      <ul className="list-unstyled">
                        {article.referenceLinks.map((link, index) => (
                          <li key={index} className="mb-2">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none text-zto"
                              title={link.url}
                            >
                              <i className="fas fa-external-link-alt me-2" style={{fontSize: '0.875rem'}}></i>
                              {link.title || link.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="article-share mt-4 pt-4 border-top">
                    <h5 className="fs-6 fw-semibold text-dark mb-3">Chia sẻ bài viết:</h5>
                    <div className="share-buttons d-flex gap-3 flex-wrap">
                      <a
                        href="#"
                        className="share-btn facebook d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none"
                        title="Chia sẻ lên Facebook"
                      >
                        <i className="fab fa-facebook-f"></i>
                      </a>
                      <a
                        href="#"
                        className="share-btn twitter d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none"
                        title="Chia sẻ lên Twitter"
                      >
                        <i className="fab fa-twitter"></i>
                      </a>
                      <a
                        href="#"
                        className="share-btn linkedin d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none"
                        title="Chia sẻ lên LinkedIn"
                      >
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                      <a
                        href="#"
                        className="share-btn email d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none"
                        title="Gửi qua Email"
                      >
                        <i className="far fa-envelope"></i>
                      </a>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          )}
        </div>

        {article && relatedArticles.length > 0 && (
          <div className="container py-5">
            <NewsSlider title="Bài viết liên quan" items={relatedArticles} />
          </div>
        )}
      </div>

      <CTASection />

      <Footer />
    </div>
  );
};

export default NewsDetail;
